/* Repository path: blue_brief_agentic_workflow/frontend/src/components/PlanItemCard.tsx */


import type { PlanItem, PlanningBlock } from "../types";
import { formatCurrency } from "../utils/format";
import { ExplainerDisclosureList } from "./ExplainerDisclosure";
import { budgetContextFor } from "../features/estimates/itemBudgetContext";
import { ItemReminderLine, type SetItemReminder } from "./ItemReminder";
import { StatusChip } from "./StatusChip";
import { TimingLine } from "./TimingLine";
/**
 * One item card, in the variant matching its calculation state.
 *
 * There is no map from item code to label here. Every display string arrives
 * from the server, because the catalogue is the single source of truth and the
 * client cannot know all of it.
 */

const CHIP: Record<
  PlanItem["calculation_state"],
  { tone: "info" | "neutral" | "warning"; label: string }
> = {
  calculated: { tone: "info", label: "Estimate" },
  needs_more_information: { tone: "neutral", label: "Needs more information" },
  identified_only: { tone: "warning", label: "Applies — check officially" },
};

const CONFIDENCE_COPY: Record<PlanItem["confidence"], string> = {
  high: "high — based on reviewed official rates for your situation",
  medium: "medium — based on reviewed official rates; confirm against your assessment",
  low: "low — calculated from reviewed inputs and rules; see the assumptions and source",
};

/** W4-06: the card's one-line summary of a recorded actual — chip plus
 * amount, nothing else. The full Estimated-vs-Actual comparison lives only
 * on the detail view (`RecordActual`); the card's job stays "what is it,
 * how much, when, what do I do". See
 * docs/design/features/W4-06.md, "Changed — PlanItemCard". */
const ACTUAL_CHIP_LABEL: Record<string, string> = {
  open: "Recorded",
  received: "Received",
  paid: "Paid",
  completed: "Done",
};

function ActualLine({ item }: { item: PlanItem }) {
  const actual = item.actual;
  if (!actual) return null;
  return (
    <p className="estimate-card__actual">
      <StatusChip tone="neutral">{ACTUAL_CHIP_LABEL[actual.state] ?? "Recorded"}</StatusChip>
      {actual.amount && <> {formatCurrency(actual.amount, actual.currency)}</>}
    </p>
  );
}

function Amount({ item }: { item: PlanItem }) {
  if (item.calculation_state === "calculated") {
    const range = item.min_amount && item.max_amount
      ? `${formatCurrency(item.min_amount, item.currency)}–${formatCurrency(item.max_amount, item.currency)}`
      : null;
    return (
      <>
        <p className="estimate-card__amount">
          {range ?? formatCurrency(item.amount as string, item.currency)}
        </p>
        <p className="estimate-card__period">
          Estimated for {item.period === "annual" ? "one year" : item.period}
        </p>
      </>
    );
  }

  if (item.calculation_state === "needs_more_information") {
    return (
      <p className="estimate-card__uncalculated">
        We need {item.missing_input?.label} to estimate this.
      </p>
    );
  }

  return (
    <p className="estimate-card__uncalculated">
      This applies to you. We don’t calculate this amount yet.
    </p>
  );
}

export function PlanItemCard({
  item,
  onImprove,
  planning = null,
  editableTiming = false,
  onSaveDeadline,
  onRemoveDeadline,
  onSetReminder,
  online = true,
}: {
  item: PlanItem;
  onImprove?: (item: PlanItem) => void;
  /** W4-05: the plan's optional `planning` block, passed straight through
   * so this card and `PlanItemDetail` derive the same reserve/allowance/
   * risk treatment from the same function — see
   * `../features/estimates/itemBudgetContext`. Replaces the pre-W4-05
   * `isUnbudgetedRisk` boolean: that flag was one purpose-built derivation
   * of this exact data for this exact card, computed by `EstimateView`
   * and never used anywhere else. Absent or `null` (the default) is a
   * valid state — plenty of items have no planning block at all — and
   * degrades to the same "nothing to say" treatment `budgetContextFor`
   * already gives a missing planning block. */
  planning?: PlanningBlock | null;
  /** W4-04: true only on a saved plan — a preview shows the declared
   * timing but offers no deadline control. Defaults to `false` so every
   * existing caller renders unchanged. See
   * docs/design/features/W4-04.md "UI/component delta". */
  editableTiming?: boolean;
  onSaveDeadline?: (itemCode: string, deadline: string) => Promise<void>;
  onRemoveDeadline?: (itemCode: string) => Promise<void>;
  /** W5-02: present only on a saved plan — a preview never shows the
   * per-item reminder line at all, the same "presence gates the control"
   * discipline `onSaveDeadline` already establishes. See
   * docs/design/features/W5-02.md "UI/component delta". */
  onSetReminder?: SetItemReminder;
  /** W5-02: threaded down from `SavedPlanPage`; defaults to `true` so
   * every other caller (a preview) renders exactly as before. */
  online?: boolean;
}) {
  const chip = CHIP[item.calculation_state];
  const budgetContext = budgetContextFor(item, planning);

  return (
    <article
      id={`item-${item.code}`}
      className={`estimate-card estimate-card--${item.calculation_state}`}
      tabIndex={-1}
    >
      <header>
        <StatusChip tone={chip.tone}>{chip.label}</StatusChip>
        <h3>
          {item.official_name} <span className="estimate-card__gloss">({item.english_name})</span>
        </h3>
      </header>

      <Amount item={item} />

      <TimingLine
        itemCode={item.code}
        timing={item.timing}
        editable={editableTiming}
        onSave={onSaveDeadline}
        onRemove={onRemoveDeadline}
      />

      <ItemReminderLine item={item} onSetReminder={onSetReminder} online={online} />

      <ActualLine item={item} />

      <dl className="facts">
        {item.calculation_state === "calculated" && (item.monthly_equivalent || item.monthly_min_amount) && (
          <div>
            <dt>Monthly equivalent</dt>
            <dd>{item.monthly_equivalent
              ? formatCurrency(item.monthly_equivalent, item.currency)
              : `${formatCurrency(item.monthly_min_amount as string, item.currency)}–${formatCurrency(item.monthly_max_amount as string, item.currency)}`}</dd>
          </div>
        )}
        <div>
          <dt>Confidence</dt>
          <dd>{CONFIDENCE_COPY[item.confidence]}</dd>
        </div>
        {item.rule_version && (
          <div>
            <dt>Rule version</dt>
            <dd>{item.rule_version}</dd>
          </div>
        )}
      </dl>

      {/* Up to two per card — see docs/design/features/W4-01.md "Density
          budget". Placed here, right after the facts a beginner needs to
          orient on and before any reserve note or action, rather than
          inside the collapsed calculation `details` below where it would
          be buried under content most users never open. */}
      <ExplainerDisclosureList explainerKeys={item.explainer_keys} max={2} />

      {item.calculation_state === "calculated" && budgetContext.kind === "in_reserve" && (
        <p className="estimate-card__reserve-note">Included in your monthly reserve.</p>
      )}
      {item.calculation_state === "calculated" && budgetContext.kind === "benefit" && (
        <p className="estimate-card__reserve-note">
          Included in potential benefits, never subtracted from payments or reserve.
        </p>
      )}
      {item.calculation_state === "calculated" && budgetContext.kind === "calculated_not_in_reserve" && (
        <p className="estimate-card__reserve-note">
          Calculated, but not included in the monthly reserve for this budgeting period.
        </p>
      )}

      {/* W4-02: a budgeting note, not an amount — never inside
          `estimate-card__amount`. See
          docs/design/features/W4-02.md "PlanItemCard delta". */}
      {(budgetContext.kind === "planning_allowance" || budgetContext.kind === "one_time_allowance") && (
        <p className="estimate-card__reserve-note">
          <StatusChip tone="neutral">Planning allowance</StatusChip>{" "}
          A conservative amount to set aside. This is not a bill, an assessment or what you will
          owe.
        </p>
      )}
      {budgetContext.kind === "unbudgeted_risk" && (
        <p className="estimate-card__reserve-note">We don’t put a number on this one yet.</p>
      )}
      {budgetContext.kind === "not_in_reserve" && (
        <p className="estimate-card__reserve-note">Not included in your monthly reserve.</p>
      )}

      {item.calculation_state === "needs_more_information" && onImprove && (
        <div className="estimate-card__refine">
          <button type="button" onClick={() => onImprove?.(item)} aria-describedby={`refine-${item.code}`}>
            Improve my estimates
          </button>
          <p className="field-hint" id={`refine-${item.code}`}>
            We’ll ask you for {item.missing_input?.label} to sharpen this estimate.
          </p>
          <ExplainerDisclosureList
            explainerKeys={item.missing_input?.explainer_keys}
            max={2}
          />
        </div>
      )}

      {item.next_action && (
        <p className="estimate-card__next-action">
          <strong>What to do:</strong> {item.next_action}
        </p>
      )}

      {/* W4-05: the card's former inline `<details>` — breakdown,
          assumptions and the full source list — moved to a dedicated
          per-item detail view (D-W4-05-04). This is the card's one and
          only door to it; the accessible name always includes the item's
          own English name so a screen-reader user listing links can tell
          them apart. See docs/design/features/W4-05.md "UI/component
          delta" and docs/architecture/adr/ADR-0009 for why the item code
          travels in the fragment, not the href's path. */}
      <a href={`#detail/${item.code}`} className="estimate-card__detail-link">
        Open full details for {item.english_name}
      </a>
    </article>
  );
}








